import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  RowSelectionState,
  getFacetedUniqueValues
} from '@tanstack/react-table';
import { Fragment, useEffect, useState } from 'react';
import { FilledButton } from '../Buttons';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions
} from '@headlessui/react';
import { cn } from 'shared/lib';
import { BiChevronDown } from 'react-icons/bi';
import { IndeterminateCheckbox } from '../IndeterminateCheckbox';
import { Filter } from './Filter';

interface ColumnMeta {
  enableColumnFilter?: boolean;
  isCollapsible?: boolean;
}

interface DataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  rowSelection?: RowSelectionState;
  enableRowSelection?: boolean;
  enablePagination?: boolean;
  getRowClassName?: (row: TData) => string;
  onChangeSelection?: (selectedRows: TData[]) => void;
  clientFiltering?: boolean;
}

const pageSizeOptions = [5, 10, 20, 50, 100];

export function DataTable<TData>({
  data,
  columns,
  getRowClassName,
  enablePagination = false,
  enableRowSelection,
  onChangeSelection,
  clientFiltering
}: DataTableProps<TData>) {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [sortingState, setSortingState] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: pageSizeOptions[0]
  });

  const collapsibleColumns = columns
    .filter((col) => (col.meta as ColumnMeta)?.isCollapsible)
    .map((col) => col.id as string);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    Object.fromEntries(collapsibleColumns.map((col) => [col, false]))
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: clientFiltering ? getFilteredRowModel() : undefined,
    getFacetedUniqueValues: clientFiltering
      ? getFacetedUniqueValues()
      : undefined,
    onSortingChange: setSortingState,
    onRowSelectionChange: enableRowSelection ? setRowSelection : undefined,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    enableRowSelection,
    state: {
      rowSelection: enableRowSelection ? rowSelection : undefined,
      sorting: sortingState,
      columnFilters: clientFiltering ? columnFilters : undefined,
      columnVisibility,
      pagination
    },
    getPaginationRowModel: enablePagination
      ? getPaginationRowModel()
      : undefined
  });

  const hiddenColumns = collapsibleColumns.filter(
    (col) => columnVisibility[col] === false
  );

  useEffect(() => {
    if (onChangeSelection) {
      const selectedRows = table
        .getSelectedRowModel()
        .rows?.map((row) => row.original);
      onChangeSelection(selectedRows);
    }
  }, [rowSelection, table, onChangeSelection]);

  return (
    <div className="ag-w-full">
      <div className="w-full overflow-auto ag-h-[34.5rem] border border-gray-300">
        <table className="min-w-full">
          <thead className="bg-gray-100 sticky top-0 z-10 ">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-4 py-2 text-left cursor-pointer select-none"
                  >
                    {header.column.getCanFilter() && clientFiltering ? (
                      <div className="relative inline-block">
                        <Filter column={header.column} table={table} />
                      </div>
                    ) : null}
                    <span onClick={header.column.getToggleSortingHandler()}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </span>

                    {header.column.getIsSorted() === 'asc'
                      ? ' ▲'
                      : header.column.getIsSorted() === 'desc'
                      ? ' ▼'
                      : ''}
                  </th>
                ))}
                {hiddenColumns.length > 0 && <th className="px-4 py-2"></th>}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <Fragment key={row.id}>
                <tr
                  className={cn(
                    `border-b`,
                    getRowClassName ? getRowClassName(row.original) : ''
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      style={{ width: cell.column.getSize() + 'px' }}
                      key={cell.id}
                      className="px-4 py-2"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {enablePagination && (
        <>
          {enableRowSelection && (
            <div className="ag-mt-4 ag-h-5 ag-flex ag-items-center ag-gap-4">
              <div className="ag-flex ag-items-center ag-gap-2">
                <IndeterminateCheckbox
                  {...{
                    checked: table.getIsAllPageRowsSelected(),
                    indeterminate: table.getIsSomePageRowsSelected(),
                    onChange: table.getToggleAllPageRowsSelectedHandler()
                  }}
                />
                <div>Page Rows ({table.getRowModel().rows.length})</div>
              </div>
              |<div>Selected: {table.getSelectedRowModel().rows?.length}</div>
            </div>
          )}

          <div className="ag-flex ag-flex-col ag-gap-3 sm:ag-flex-row ag-justify-between ag-items-center">
            <div className="ag-mb-2 sm:ag-mb-0 ag-flex ag-gap-3">
              <span>Total: {data.length}</span>
            </div>
            <div className="ag-flex ag-items-center ag-gap-4">
              <span>Rows per page:</span>
              <Listbox
                value={pagination.pageSize}
                onChange={(newSize) => {
                  setPagination({
                    pageSize: newSize,
                    pageIndex: 0
                  });
                }}
              >
                <div className="ag-relative">
                  <ListboxButton className="ag-flex ag-items-center ag-gap-2 ag-border ag-border-gray-300 ag-p-1 ag-rounded">
                    {pagination.pageSize}
                    <BiChevronDown />
                  </ListboxButton>
                  <ListboxOptions
                    anchor="top end"
                    className="ag-absolute ag-mt-1 ag-bg-white ag-border ag-shadow-lg ag-rounded"
                  >
                    {pageSizeOptions.map((size) => (
                      <ListboxOption
                        key={size}
                        value={size}
                        className="ag-cursor-pointer ag-px-4 ag-py-2 hover:ag-bg-gray-200"
                      >
                        {size}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </div>
              </Listbox>
            </div>
            <div className="ag-flex ag-items-center ag-gap-2">
              <FilledButton
                className="ag-px-4 ag-py-2"
                onClick={() => {
                  table.previousPage();
                }}
                disabled={!table.getCanPreviousPage()}
              >
                Back
              </FilledButton>
              <span>
                Page {table.getState().pagination.pageIndex + 1} of{' '}
                {table.getPageCount()}
              </span>
              <FilledButton
                className="ag-px-4 ag-py-2"
                onClick={() => {
                  table.nextPage();
                }}
                disabled={!table.getCanNextPage()}
              >
                Next
              </FilledButton>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
